
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, FormControlLabel, Switch, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Chip } from '@mui/material';
import { Person as PersonIcon, AccountBalance as BankingIcon, Payment as PaymentIcon, List as ListIcon } from '@mui/icons-material';
import FormSection from './FormSection.jsx';
import FormSelect from './FormSelect.jsx';
import SimpleInput from '../Input/SimpleInput.jsx';
import CurrencyInput from './CurrencyInput.jsx';
import { getAttributesByTypeName } from '../../utils/attributes';
import DynamicAttributeRenderer from './DynamicAttributeRenderer';

const ProfileStep = ({ merchantData, isEditMode, missingItems, formData = {}, updateFormData, validationErrors = {} }) => {
  // Helper function to get attributes by type name from merchant data (same logic as getAttributesByTypeName)
  const getAttributesByTypeNameFromMerchantData = (typeName) => {
    // Check both possible structures: merchantData.data.attribute_types or merchantData.attribute_types
    const attributeTypes = merchantData?.data?.attribute_types || merchantData?.attribute_types;

    if (!merchantData || !attributeTypes || !Array.isArray(attributeTypes)) {

      return [];
    }

    const attributeType = attributeTypes.find(type =>
      type.name && type.name.toLowerCase() === typeName.toLowerCase()
    );

    console.log(merchantData, 'checking the dataa');


    // Transform attributes to use UUID as ID in edit mode
    const attributes = attributeType ? attributeType.attributes || [] : [];
    return attributes.map(attr => ({
      ...attr,
      id: attr.id  // Use UUID as the ID for ProfileStep component
    }));
  };
  // Merchant Information State
  const [federalTaxId, setFederalTaxId] = useState(formData?.tax_id || merchantData?.federal_tax_id || '');
  const [numberOfLocations, setNumberOfLocations] = useState(formData?.number_of_locations?.toString() || merchantData?.no_locations?.toString() || '1');
  const [yearsInBusiness, setYearsInBusiness] = useState(formData?.years_in_business?.toString() || merchantData?.business_years?.toString() || '');
  const [monthsInBusiness, setMonthsInBusiness] = useState(formData?.months_in_business?.toString() || merchantData?.business_months?.toString() || '');
  const [ownership, setOwnership] = useState(formData?.ownership_type || merchantData?.ownership || '');
  const [productServicesCategory, setProductServicesCategory] = useState(formData?.business_category || merchantData?.business_category || '');
  const [averageMonthlySales, setAverageMonthlySales] = useState(formData?.avg_month_sales?.toString() || merchantData?.avg_month_sales?.toString() || '');
  const [businessType, setBusinessType] = useState(formData?.business_type || merchantData?.business_type || '');
  const [returnPolicy, setReturnPolicy] = useState(formData?.return_policy || merchantData?.return_policy || '');
  const [webSiteAddress, setWebSiteAddress] = useState(formData?.website || merchantData?.website || '');
  const [daysToDelivery, setDaysToDelivery] = useState(formData?.delivery_days || merchantData?.delivery_days || '');

  const [bussinessTitle, setBussinessTitle] = useState(formData?.bussiness_name || formData?.merchant?.business_name || '');
  const [bussinessdesc, setBussinessdesc] = useState(formData?.business_description || formData?.merchant?.business_description || '');
  const [midNumber, setMidNumber] = useState(formData?.mid_number || merchantData?.merchant?.mid_number || merchantData?.data?.merchant?.mid_number || '');

  // Banking Information State
  const [bankName, setBankName] = useState(formData?.banking?.bank_name || merchantData?.bank_name || '');
  const [accountOwnership, setAccountOwnership] = useState(formData?.banking?.account_owner || merchantData?.account_owner || '');
  const [authorizedSignerFirstName, setAuthorizedSignerFirstName] = useState(formData?.banking?.full_name?.split(' ')[0] || merchantData?.full_name?.split(' ')[0] || '');
  const [authorizedSignerLastName, setAuthorizedSignerLastName] = useState(formData?.banking?.full_name?.split(' ')[1] || merchantData?.full_name?.split(' ')[1] || ''); // Added assuming you need full name; if not, remove.
  const [businessName, setBusinessName] = useState(formData?.banking?.business_name || merchantData?.business_name || '');
  const [accounttitle, setAccounttitle] = useState(formData?.banking?.title || merchantData?.title || '');

  const [creditRoutingNumber, setCreditRoutingNumber] = useState(formData?.banking?.routing_number || merchantData?.routing_number || '');
  const [accountType, setAccountType] = useState(formData?.banking?.account_type || merchantData?.account_type || '');
  const [creditAccountNumber, setCreditAccountNumber] = useState(formData?.banking?.account_number || merchantData?.account_number || '');
  const [repeatCreditAccountNumber, setRepeatCreditAccountNumber] = useState(formData?.banking?.confirm_account_number || merchantData?.confirm_account_number || '');
  const [differentAccountForFees, setDifferentAccountForFees] = useState(formData?.banking?.fee_deduct_other || merchantData?.fee_deduct_other || false);

  // Payment Methods State
  const [cardPresentTransaction, setCardPresentTransaction] = useState(formData?.banking?.present_transaction?.toString() || merchantData?.present_transaction?.toString() || '');
  const [cardMotoTransaction, setCardMotoTransaction] = useState(formData?.banking?.moto_transaction?.toString() || merchantData?.moto_transaction?.toString() || '');
  const [internet, setInternet] = useState(formData?.banking?.ecom_transaction?.toString() || merchantData?.ecom_transaction?.toString() || '');

  // New state for dynamic attributes
  const [dynamicAttributes, setDynamicAttributes] = useState({});
  const [otherInformationAttributes, setOtherInformationAttributes] = useState([]);
  const [siteInspectionAttributes, setSiteInspectionAttributes] = useState([]);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(true);
  const isInitialized = useRef(false);

  // Load attribute types on component mount
  useEffect(() => {
    loadDynamicAttributes();
  }, [merchantData]);


  const loadDynamicAttributes = async () => {
    try {
      setIsLoadingAttributes(true);

      let otherInfoAttributes = [];
      let siteInspectionAttributes = [];

      // Check if we have merchantData (edit mode) - use merchant data instead of API
      const merchantAttributeTypes = merchantData?.data?.attribute_types || merchantData?.attribute_types;

      if (merchantData && merchantAttributeTypes && Array.isArray(merchantAttributeTypes)) {

        // Load "Other Information" attributes from merchant data
        otherInfoAttributes = getAttributesByTypeNameFromMerchantData("Other Information");
        setOtherInformationAttributes(otherInfoAttributes);


        // Load "Site Inspection" attributes from merchant data
        siteInspectionAttributes = getAttributesByTypeNameFromMerchantData("Site Inspection");
        setSiteInspectionAttributes(siteInspectionAttributes);

      } else {

        // Load "Other Information" attributes from API
        otherInfoAttributes = getAttributesByTypeName("Other Information");
        setOtherInformationAttributes(otherInfoAttributes);

        // Load "Site Inspection" attributes from API
        siteInspectionAttributes = getAttributesByTypeName("Site Inspection");
        setSiteInspectionAttributes(siteInspectionAttributes);
      }

      // Combine all attributes for initialization
      const allAttributes = [...otherInfoAttributes, ...siteInspectionAttributes];

      if (allAttributes.length > 0) {
        // Initialize dynamic attributes state with existing values or defaults
        const initialAttributes = {};
        allAttributes.forEach(attr => {
          // Use existing value from merchantData or set default based on type
          if (dynamicAttributes[attr.id] !== undefined) {
            initialAttributes[attr.id] = dynamicAttributes[attr.id];
          } else {
            // Set default values based on type
            if (attr.type === 'checkbox') {
              initialAttributes[attr.id] = false;
            } else if (attr.type === 'text' || attr.type === 'select') {
              initialAttributes[attr.id] = '';
            } else if (attr.type === 'number') {
              initialAttributes[attr.id] = 0;
            }
          }
        });
        setDynamicAttributes(initialAttributes);
      }
    } catch (error) {
      console.error('Error loading attribute types:', error);
    } finally {
      setIsLoadingAttributes(false);
    }
  };

  // Handle dynamic attribute changes
  const handleAttributeChange = (attributeId, value) => {
    setDynamicAttributes(prev => {
      const currentValueOrObject = prev[attributeId];

      // Preserve UUID structure in edit mode
      const newValue = typeof currentValueOrObject === 'object' && currentValueOrObject.uuid
        ? { value: value, uuid: currentValueOrObject.uuid }  // Keep UUID structure in edit mode
        : value;  // Use simple string value in create mode

      return {
        ...prev,
        [attributeId]: newValue
      };
    });
  };

  // Update formData when local state changes
  useEffect(() => {
    if (updateFormData) {
      const fullName = `${authorizedSignerFirstName} ${authorizedSignerLastName}`.trim();
      const currentData = {
        tax_id: federalTaxId,
        number_of_locations: parseInt(numberOfLocations) || 1,
        years_in_business: parseInt(yearsInBusiness) || 0,
        months_in_business: parseInt(monthsInBusiness) || 0,
        ownership_type: ownership,
        business_category: productServicesCategory,
        avg_month_sales: parseFloat(averageMonthlySales) || 0,
        annual_revenue: (parseFloat(averageMonthlySales) || 0) * 12,
        business_type: businessType,
        return_policy: returnPolicy,
        business_name: bussinessTitle,
        delivery_days: daysToDelivery,
        business_description: bussinessdesc,
        website: webSiteAddress,
        mid_number: midNumber,
        industry: productServicesCategory, // Assuming this is the industry
        banking: {
          bank_name: bankName,
          routing_number: creditRoutingNumber,
          account_number: creditAccountNumber,
          confirm_account_number: repeatCreditAccountNumber,
          account_type: accountType,
          account_owner: accountOwnership,
          full_name: fullName,
          title: accounttitle,
          bussiness_name: businessName,
          fee_deduct_other: differentAccountForFees,
          present_transaction: Math.round(parseFloat(cardPresentTransaction) || 0),
          moto_transaction: Math.round(parseFloat(cardMotoTransaction) || 0),
          ecom_transaction: Math.round(parseFloat(internet) || 0),
        },
        dynamic_attributes: dynamicAttributes, // Will merge with FeesStep's dynamic_attributes via deep merge
      };

      updateFormData(currentData);
    }
  }, [
    federalTaxId, numberOfLocations, yearsInBusiness, monthsInBusiness, ownership, productServicesCategory,
    averageMonthlySales, businessType, returnPolicy, webSiteAddress, daysToDelivery,
    bankName, accountOwnership, authorizedSignerFirstName, authorizedSignerLastName, businessName,
    creditRoutingNumber, accountType, creditAccountNumber, repeatCreditAccountNumber, differentAccountForFees,
    cardPresentTransaction, cardMotoTransaction, internet, dynamicAttributes, bussinessTitle, bussinessdesc, accounttitle, midNumber
  ]);

  // Sync local state with formData prop when navigating between steps
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0 && !isInitialized.current) {
      setFederalTaxId(formData.tax_id || '');
      setNumberOfLocations(formData.number_of_locations?.toString() || '1');
      setYearsInBusiness(formData.years_in_business?.toString() || '');
      setMonthsInBusiness(formData.months_in_business?.toString() || '');
      setOwnership(formData.ownership_type || '');
      setProductServicesCategory(formData.business_category || '');
      setAverageMonthlySales(formData.avg_month_sales?.toString() || '');
      setBusinessType(formData.business_type || '');
      setReturnPolicy(formData.return_policy || '');
      setWebSiteAddress(formData.website || '');
      setDaysToDelivery(formData.delivery_days || '');
      setBussinessTitle(formData?.business_name || '');
      setBussinessdesc(formData?.business_description || '');
      setMidNumber(formData?.mid_number || '');

      // Banking information
      setBankName(formData.banking?.bank_name || '');
      setAccounttitle(formData?.banking?.title || '');
      setAccountOwnership(formData.banking?.account_owner || '');
      setAuthorizedSignerFirstName(formData.banking?.full_name?.split(' ')[0] || '');
      setAuthorizedSignerLastName(formData.banking?.full_name?.split(' ')[1] || '');
      setBusinessName(formData.banking?.business_name || '');
      setCreditRoutingNumber(formData.banking?.routing_number || '');
      setAccountType(formData.banking?.account_type || '');
      setCreditAccountNumber(formData.banking?.account_number || '');
      setRepeatCreditAccountNumber(formData.banking?.confirm_account_number || '');
      setDifferentAccountForFees(formData.banking?.fee_deduct_other || false);
      setCardPresentTransaction(formData.banking?.present_transaction?.toString() || '');
      setCardMotoTransaction(formData.banking?.moto_transaction?.toString() || '');
      setInternet(formData.banking?.ecom_transaction?.toString() || '');

      // Dynamic attributes - need to map from numeric ID to UUID
      if (formData.dynamic_attributes) {
        // Check if we're in edit mode and need to map IDs
        const merchantAttributeTypes = merchantData?.data?.attribute_types || merchantData?.attribute_types;
        if (merchantData && merchantAttributeTypes && Array.isArray(merchantAttributeTypes)) {
          // Create mapping from numeric ID to UUID
          const idToUuidMap = {};
          merchantAttributeTypes
            .flatMap(type => type.attributes || [])
            .forEach(attr => {
              idToUuidMap[attr.id] = attr.id; // Map numeric ID to UUID
            });

          // Map formData values from numeric ID keys to UUID keys
          const mappedDynamicAttributes = {};
          Object.entries(formData.dynamic_attributes).forEach(([numericId, valueOrObject]) => {
            const id = idToUuidMap[numericId];
            if (id) {
              mappedDynamicAttributes[id] = valueOrObject;
            }
          });

          setDynamicAttributes(mappedDynamicAttributes);


        } else {
          // Create mode - use as is
          setDynamicAttributes(formData.dynamic_attributes);
        }
      }
      isInitialized.current = true;
    }
  }, [formData]);

  const ownershipOptions = [
    { value: 'LLC Private', label: 'LLC Private' },
    { value: 'Corporation', label: 'Corporation' },
    { value: 'Partnership', label: 'Partnership' },
    { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
    { value: 'LLC Public', label: 'LLC Public' }
  ];

  const productServicesOptions = [
    { value: 'Financial Services', label: 'Financial Services' },
    { value: 'Retail', label: 'Retail' },
    { value: 'Restaurant', label: 'Restaurant' },
    { value: 'Services', label: 'Services' },
    { value: 'E-commerce', label: 'E-commerce' },
    { value: 'Other', label: 'Other' }
  ];

  const daysToDeliveryOptions = [
    { value: '0-3', label: '0-3' },
    { value: '4-7', label: '4-7' },
    { value: '8-14', label: '8-14' },
    { value: '15-30', label: '15-30' },
    { value: '30+', label: '30+' }
  ];

  const businessTypeOptions = [
    { value: 'B2B', label: 'B2B' },
    { value: 'B2C', label: 'B2C' },
    { value: 'Both', label: 'Both' }
  ];

  const accountOwnershipOptions = [
    { value: 'Business Account', label: 'Business Account' },
    { value: 'Personal Account', label: 'Personal Account' }
  ];

  const accountTypeOptions = [
    { value: 'Checking', label: 'Checking' },
    { value: 'Savings', label: 'Savings' }
  ];


  // functionality of 100 % percent 
  const numA = Number(cardPresentTransaction) || 0;
  const numB = Number(cardMotoTransaction) || 0;
  const numC = Number(internet) || 0;

  const sum = numA + numB + numC;
  const totals100 = Math.abs(sum - 100) < 0.01; // allow tiny float imprecision

  const showTotalError = !totals100 && (numA > 0 || numB > 0 || numC > 0);
  const hasPaymentSumError = !!validationErrors.banking_payment_methods_sum;

  // Auto-adjust: when the three percentages exceed 100%, scale them to total 100%. All values are integers.
  const applyPaymentPercent = (field, value) => {
    const num = parseFloat(value) || 0;
    let present = field === 'present' ? num : numA;
    let moto = field === 'moto' ? num : numB;
    let ecom = field === 'ecom' ? num : numC;
    const total = present + moto + ecom;
    if (total > 100 && total > 0) {
      const scale = 100 / total;
      present = Math.round(present * scale);
      moto = Math.round(moto * scale);
      ecom = 100 - present - moto; // keeps sum exactly 100
    } else {
      present = Math.round(present);
      moto = Math.round(moto);
      ecom = Math.round(ecom);
    }
    setCardPresentTransaction(String(present));
    setCardMotoTransaction(String(moto));
    setInternet(String(ecom));
  };

  const internetError =
    !!validationErrors.banking_ecom_transaction ||
    (showTotalError && "The three percentages must sum to 100%");


  return (
    <Box sx={{ p: 3 }}>
      {/* Merchant Information Section */}
      <FormSection
        icon={<PersonIcon />}
        title="Business Information"
        bgColor="blue.50"
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <SimpleInput
            type="text"
            name="merchants[federal_tax_id]"
            value={federalTaxId}
            onChange={(e) => setFederalTaxId(e.target.value)}
            placeholder="Enter Federal Tax ID/EIN "
            label="Federal Tax ID/EIN "
            required={true}
            error={!!validationErrors.merchant_federal_tax}
            errorMessage={validationErrors?.merchant_federal_tax}
          />
          <SimpleInput
            type="text"
            name="merchants[mid_number]"
            value={midNumber}
            onChange={(e) => setMidNumber(e.target.value)}
            placeholder="Enter Mid number"
            label="Mid number"
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 2 }}>
          <SimpleInput
            type="number"
            name="merchants[business_years]"
            value={yearsInBusiness}
            onChange={(e) => setYearsInBusiness(e.target.value)}
            placeholder="Enter Years in business"
            label="Years in business"
          />
          <SimpleInput
            type="number"
            name="merchants[business_months]"
            value={monthsInBusiness}
            onChange={(e) => setMonthsInBusiness(e.target.value)}
            placeholder="Enter Months In Business"
            label="Months In Business"
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 2 }}>
          <FormSelect
            label="Ownership"
            name={"merchants[ownership_type]"}
            value={ownership}
            onChange={(e) => setOwnership(e.target.value)}
            options={ownershipOptions}
            placeholder="Select an option"
            searchable={true}
          />
          <FormSelect
            label="Product/Services Category"
            name={"merchants[business_category]"}
            value={productServicesCategory}
            onChange={(e) => setProductServicesCategory(e.target.value)}
            options={productServicesOptions}
            placeholder="Select an option"
            searchable={true}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 2 }}>
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 500,
                color: 'grey.700',
                fontSize: '0.875rem'
              }}
            >
              Average transaction size
            </Typography>
            <CurrencyInput
              name={"merchants[avg_month_sales]"}
              value={averageMonthlySales}
              onChange={setAverageMonthlySales}
              placeholder="0.00"
              width={200}
            />
          </Box>

          <SimpleInput
            type="text"
            name="merchants[business_name]"
            value={bussinessTitle}
            onChange={(e) => setBussinessTitle(e.target.value)}
            placeholder="Enter bussiness title"
            label="Bussiness Title"
          />
          <SimpleInput
            type="text"
            name="merchants[business_description]"
            value={bussinessdesc}
            onChange={(e) => setBussinessdesc(e.target.value)}
            placeholder="Enter bussiness description"
            label="Bussiness description"
          />

          <SimpleInput
            type="text"
            name="merchants[return_policy]"
            value={returnPolicy}
            onChange={(e) => setReturnPolicy(e.target.value)}
            placeholder="Enter Return Policy"
            label="Return Policy"
          />

        </Box>



        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 2 }}>
          <Box>
            <SimpleInput
              type="url"
              name="merchants[website]"
              value={webSiteAddress}
              onChange={(e) => setWebSiteAddress(e.target.value)}
              placeholder="Enter Web Site Address (e.g., https://www.example.com)"
              label="Web Site Address"

            />
          </Box>
          <Box sx={{ mt: '26px' }}>
            <FormSelect
              name={"merchants[delivery_days]"}
              label="Days to Delivery"
              value={daysToDelivery}
              onChange={(e) => setDaysToDelivery(e.target.value)}
              options={daysToDeliveryOptions}
              placeholder="Select an option"
              searchable={true}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 3, mt: 2 }}>

        </Box>
      </FormSection>

      {/* Banking Information Section */}
      <FormSection
        icon={<BankingIcon />}
        title="Banking Information"
        bgColor="grey.50"
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <SimpleInput
            type="text"
            name="bank_account[bank_name]"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="Enter Bank Name"
            label="Bank Name"
            required={true}
            error={!!validationErrors.banking_bank_name}
            errorMessage={validationErrors.banking_bank_name}
          />
          {/* <Box sx={{ mt: '30px' }}>
            <FormSelect
              label="Account Ownership"
              name="bank_account[account_owner]"
              value={accountOwnership}
              onChange={(e) => {
                setAccountOwnership(e.target.value)
              }}
              options={accountOwnershipOptions}
              placeholder="Select an option"
              searchable={true}
              required={true}
              error={!!validationErrors.banking_account_owner}
              errorMessage={validationErrors.banking_account_owner}
            />
          </Box> */}
          {/* <SimpleInput
            type="text"
            name="bank_account[first_name]"
            value={authorizedSignerFirstName}
            onChange={(e) => setAuthorizedSignerFirstName(e.target.value)}
            placeholder="Enter Authorized Signer First Name"
            label="Authorized Signer First Name" // Updated label for clarity
            required={true}
            error={!!validationErrors.banking_full_name}
            errorMessage={validationErrors.banking_full_name}
          />
          <SimpleInput
            type="text"
            name="bank_account[last_name]"
            value={authorizedSignerLastName}
            onChange={(e) => setAuthorizedSignerLastName(e.target.value)}
            placeholder="Enter Authorized Signer Last Name"
            label="Authorized Signer Last Name" // Added field
            required={true}
            error={!!validationErrors.banking_full_name}
            errorMessage={validationErrors.banking_full_name}
          /> */}
          <SimpleInput
            type="text"
            name="bank_account[business_name]"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Enter Business Name"
            label="Business Name"
          />

          {/* title of account */}
          <SimpleInput
            type="text"
            name="bank_account[title]"
            value={accounttitle}
            onChange={(e) => setAccounttitle(e.target.value)}
            placeholder="Enter Account Title"
            label="Account Title"
          />
          {/* end of titile of account */}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 2 }}>
          <SimpleInput
            type="text"
            name="bank_account[routing_number]"
            value={creditRoutingNumber}
            onChange={(e) => setCreditRoutingNumber(e.target.value)}
            placeholder="Enter Routing Number"
            label="Routing Number"
            required={true}
            maxLength='9'
            error={!!validationErrors.banking_routing_number}
            errorMessage={validationErrors.banking_routing_number}
          />
          <Box sx={{ mt: '30px' }}>
            <FormSelect
              label="Account Type"
              name="bank_account[account_type]"
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              options={accountTypeOptions}
              placeholder="Select an option"
              searchable={true}
              required={true}
              error={!!validationErrors.banking_account_type}
              errorMessage={validationErrors.banking_account_type}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 2 }}>
          <SimpleInput
            type="text"
            name="bank_account[account_number]"
            value={creditAccountNumber}
            onChange={(e) => setCreditAccountNumber(e.target.value)}
            placeholder="Enter Account Number"
            label="Account Number"
            required={true}
            error={!!validationErrors.banking_account_number}
            errorMessage={validationErrors.banking_account_number}
          />
          {/* <SimpleInput
            type="text"
            name="bank_account[confirm_account_number]"
            value={repeatCreditAccountNumber}
            onChange={(e) => setRepeatCreditAccountNumber(e.target.value)}
            placeholder="Enter Repeat Credit Account Number"
            label="Repeat Credit Account Number"
            error={!!validationErrors.banking_confirm_account_number}
            errorMessage={validationErrors.banking_confirm_account_number}
          /> */}
        </Box>

        <Box sx={{ mt: 2 }}>
          {/* <FormControlLabel
            control={
              <Switch
                name="bank_account[fee_deduct_other]"
                checked={differentAccountForFees}
                onChange={(e) => setDifferentAccountForFees(e.target.checked)}
              />
            }
            label="Would you like the processing fees to be deducted from a different account?"
          /> */}
        </Box>
      </FormSection>

      {/* Payment Methods Section */}
      <FormSection
        icon={<PaymentIcon />}
        title="Payment Methods"
        bgColor="grey.50"
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 140 }}>Card Present Transaction %</Typography>
            <SimpleInput
              type="number"
              name="bank_account[present_transaction]"
              value={cardPresentTransaction}
              onChange={(e) => applyPaymentPercent('present', e.target.value)}
              placeholder="0"
              required={true}
              error={!!validationErrors.banking_present_transaction}
              errorMessage={validationErrors.banking_present_transaction}
              sx={{ width: 80 }}
              inputProps={{ min: 0, max: 100, step: 1 }}
            />
            <Typography variant="body2" sx={{ color: 'grey.600' }}>%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 140 }}>Card MOTO Transaction %</Typography>
            <SimpleInput
              type="number"
              name="bank_account[moto_transaction]"
              value={cardMotoTransaction}
              onChange={(e) => applyPaymentPercent('moto', e.target.value)}
              placeholder="0"
              required={true}
              error={!!validationErrors.banking_moto_transaction}
              errorMessage={validationErrors.banking_moto_transaction}
              sx={{ width: 80 }}
              inputProps={{ min: 0, max: 100, step: 1 }}
            />
            <Typography variant="body2" sx={{ color: 'grey.600' }}>%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 140 }}>Internet %</Typography>
            <SimpleInput
              type="number"
              name="bank_account[ecom_transaction]"
              value={internet}
              onChange={(e) => applyPaymentPercent('ecom', e.target.value)}
              placeholder="0"
              required={true}
              error={!!validationErrors.banking_ecom_transaction}
              errorMessage={validationErrors.banking_ecom_transaction}
              sx={{ width: 80 }}
              inputProps={{ min: 0, max: 100, step: 1 }}
            />
            <Typography variant="body2" sx={{ color: 'grey.600' }}>%</Typography>
          </Box>
          {(showTotalError || hasPaymentSumError) && (
            <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / 4' }, mt: 1 }}>
              <Typography variant="caption" color="error">
                {validationErrors.banking_payment_methods_sum || `The percentages must add up to exactly 100% (currently: ${sum.toFixed(2)}%)`}
              </Typography>
            </Box>
          )}
        </Box>
      </FormSection>

      {/* Site Inspection Section - Now Dynamic */}
      {/* <FormSection
        icon={<ListIcon />}
        title="Site Inspection"
        bgColor="grey.50"
      >
        <DynamicAttributeRenderer
          attributes={siteInspectionAttributes}
          values={dynamicAttributes}
          onChange={handleAttributeChange}
          isLoading={isLoadingAttributes}
        />
      </FormSection> */}

      {/* Other Information Section - Now Dynamic */}
      {/* <FormSection
        icon={<ListIcon />}
        title="Other Information"
        bgColor="grey.50"
      >
        <DynamicAttributeRenderer
          attributes={otherInformationAttributes}
          values={dynamicAttributes}
          onChange={handleAttributeChange}
          isLoading={isLoadingAttributes}
        />
      </FormSection> */}
      {(() => {
        const rawLog = Array.isArray(merchantData?.data?.previous_log) ? merchantData.data.previous_log : [];
        const listedOnly = rawLog.filter((row) => {
          const equipment = row.equipments?.[0] ?? row;
          const modelName = equipment.equipment_model?.name ?? row.equipment_model?.name;
          const typeName = equipment.equipment_type?.name ?? row.equipment_type?.name;
          const manufacturerName = equipment.manufacturer?.name ?? row.manufacturer?.name;
          const statusName = equipment.last_status_log?.status?.name ?? row.last_status_log?.status?.name ?? row.status?.name;
          const sku = equipment.sku_number ?? row.sku_number;
          const hasModel = modelName && String(modelName).trim() !== '';
          const hasType = typeName && String(typeName).trim() !== '';
          const hasManufacturer = manufacturerName && String(manufacturerName).trim() !== '';
          const hasStatus = statusName && String(statusName).trim() !== '';
          const hasSku = sku != null && String(sku).trim() !== '';
          return hasModel || hasType || hasManufacturer || hasStatus || hasSku;
        });
        return listedOnly.length > 0 ? (
          <FormSection
            icon={<ListIcon />}
            title="Previous Log / Client Exist"
            bgColor="grey.50"
          >
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle1" fontWeight={600} color="grey.700" sx={{ mb: 2 }}>
                Previous Log List
              </Typography>

              <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                <Table sx={{ minWidth: 650 }} aria-label="previous log table">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                        Equipment Model
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                        Equipment Type
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                        Equipment Manufacturer
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                        Equipment Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                        SKU
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                        Created At
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {listedOnly.map((row) => {
                      const equipment = row.equipments?.[0] ?? row;
                      const modelName = equipment.equipment_model?.name ?? row.equipment_model?.name;
                      const typeName = equipment.equipment_type?.name ?? row.equipment_type?.name;
                      const manufacturerName = equipment.manufacturer?.name ?? row.manufacturer?.name;
                      const statusName = equipment?.equipment_last_Log?.status?.name;
                      const sku = equipment.sku_number ?? row.sku_number;
                      return (
                        <TableRow key={row.id ?? equipment.id ?? Math.random()} hover>
                          <TableCell>{modelName || 'N/A'}</TableCell>
                          <TableCell>{typeName || 'N/A'}</TableCell>
                          <TableCell>{manufacturerName || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={statusName || 'N/A'}
                              size="small"
                              color={statusName === 'Completed' ? 'success' : statusName === 'Underwriting' ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{sku ?? 'N/A'}</TableCell>
                          <TableCell>{new Date(equipment?.equipment_last_Log?.created_at).toLocaleDateString() ?? 'N/A'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </FormSection>
        ) : null;
      })()}
    </Box>
  );
};

export default ProfileStep;